/**
 * DangerSection — permanently delete the caller's account.
 *
 * Typed-email confirmation (case-insensitive). Final AlertDialog prompt.
 * On confirm: spinner + label "Deleting…", all inputs disabled, modal
 * remains visible so the user sees progress. Success → signOut locally +
 * redirect to /. Failure → toast + re-enable form.
 */

import { useState } from 'react';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'firebase/auth';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/lib/stores/authStore';
import { auth } from '@/lib/firebase';
import { deleteAccount } from '@/lib/auth-service';
import { toast } from '@/lib/toast';
import { useNavigate } from 'react-router-dom';

// Server sentinel (DELETE /api/users/me) for a too-old session. Surfaced by the
// api client as the thrown error's message; we reauthenticate and retry.
const REAUTH_REQUIRED = 'requires_recent_login';

interface DangerSectionProps {
  /** Called after successful delete so the modal can close itself. */
  onDeleted?: () => void;
}

export function DangerSection({ onDeleted }: DangerSectionProps) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Set when the server rejects the delete with `requires_recent_login`: we then
  // reveal a password field, reauthenticate, and retry with a fresh token.
  const [reauthNeeded, setReauthNeeded] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');

  if (!user) return null;

  const isAnonymous = user.isAnonymous;
  // Guests have no email; they are identified + confirm by their username.
  const username = auth.currentUser?.displayName ?? '';
  const confirmTarget = isAnonymous ? username : (user.email ?? '');
  // Reauth path depends on the provider: password users type their password,
  // OAuth (Google) users reauthenticate via popup, guests never reauth (the
  // backend skips the recent-login gate for anonymous accounts).
  const isPasswordUser =
    auth.currentUser?.providerData.some((p) => p.providerId === 'password') ?? false;
  const trimmed = typed.trim();
  const matches = confirmTarget.length > 0 && trimmed.toLowerCase() === confirmTarget.toLowerCase();
  const canDelete =
    matches && !deleting && (!reauthNeeded || !isPasswordUser || reauthPassword.length > 0);

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setDeleting(true);
    try {
      // If a prior attempt was rejected for a stale session, reauthenticate first
      // so the retried DELETE carries a fresh `auth_time`. The forced token
      // refresh is required because getIdToken() otherwise returns the cached
      // (stale-auth_time) token the server would reject again.
      if (reauthNeeded) {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          toast.error('Please sign in again to delete your account.');
          setDeleting(false);
          return;
        }
        try {
          if (isPasswordUser) {
            if (!reauthPassword) {
              toast.error('Enter your current password to continue.');
              setDeleting(false);
              return;
            }
            await reauthenticateWithCredential(
              currentUser,
              EmailAuthProvider.credential(user.email ?? '', reauthPassword)
            );
          } else {
            // OAuth (Google) account: no password — reauthenticate via popup.
            await reauthenticateWithPopup(currentUser, new GoogleAuthProvider());
          }
          await currentUser.getIdToken(true);
        } catch {
          toast.error(
            isPasswordUser
              ? 'Incorrect password. Please try again.'
              : 'Google reauthentication failed. Please try again.'
          );
          setDeleting(false);
          return;
        }
      }

      await deleteAccount(trimmed);
      toast.success('Account deleted.');
      onDeleted?.();
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof Error && err.message === REAUTH_REQUIRED) {
        setReauthNeeded(true);
        toast.error('For your security, confirm your password to delete your account.');
        setDeleting(false);
        return;
      }
      console.error('Delete account failed:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete account. Try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-destructive">Delete account</h1>
        <p className="text-sm text-muted-foreground">
          Permanently delete your account, characters, and all associated data. This cannot be
          undone.
        </p>
      </header>

      <div className="space-y-4 rounded-md border border-destructive/30 bg-destructive/5 p-4">
        <div className="flex items-start gap-2 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div className="text-muted-foreground">
            <p className="font-medium text-destructive">
              This will delete your account and everything tied to it:
            </p>
            <ul className="mt-1 list-inside list-disc space-y-0.5">
              <li>Your Firebase sign-in</li>
              <li>All your characters and ownership records</li>
              <li>Your party memberships</li>
              <li>Your passkeys</li>
            </ul>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="danger-confirm-email">
            {isAnonymous
              ? 'Type your username to confirm: '
              : 'Type your email address to confirm: '}
            <span className="font-mono text-foreground">{confirmTarget}</span>
          </Label>
          <Input
            id="danger-confirm-email"
            type={isAnonymous ? 'text' : 'email'}
            autoComplete="off"
            placeholder={isAnonymous ? 'Your username' : 'you@example.com'}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={deleting}
          />
        </div>

        {reauthNeeded && isPasswordUser && (
          <div className="space-y-2">
            <Label htmlFor="danger-reauth-password">Confirm your password</Label>
            <Input
              id="danger-reauth-password"
              type="password"
              autoComplete="current-password"
              placeholder="Current password"
              value={reauthPassword}
              onChange={(e) => setReauthPassword(e.target.value)}
              disabled={deleting}
            />
            <p className="text-xs text-muted-foreground">
              For your security, re-enter your password to delete your account.
            </p>
          </div>
        )}
        {reauthNeeded && !isPasswordUser && (
          <p className="text-xs text-muted-foreground">
            For your security, you'll confirm with Google when you click delete.
          </p>
        )}

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" disabled={!canDelete} className="gap-2">
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete my account
                </>
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete account permanently?</AlertDialogTitle>
              <AlertDialogDescription>
                This cannot be undone. Your Firebase sign-in, characters, and party memberships will
                be removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirm}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
